import { Theme, useTheme } from "@amx/ux-twoui-theming";
import { Callout as FluentCallout, IButtonStyles, ICalloutContentStyles, IconButton as FluentIconButton } from "@fluentui/react";
import React, { RefObject, useRef, useState } from "react";
import { CSSProperties } from "react";
import { AnchorPosition, anchorPositionToFluentMapping } from "../../shared/constants";
import { useComponentLogger } from "../../shared/logging";
import { BaseProps, InteractiveElementProps, validateProps } from "../../shared/props";
import { Icon } from "../Icon/Icon";
import { Label } from "../Label/Label";
import { Stack } from "../Stacks/Stack/Stack";

/**
 * Styles for the content of the Callout
 */
const calloutStyles = (theme: Theme): Partial<ICalloutContentStyles> => ({
    calloutMain: {
        backgroundColor: theme.palette.neutralHigh,
        maxWidth: "300px",
        padding: "20px 24px"
    },
    beak: {
        backgroundColor: theme.palette.neutralHigh
    }
});

export interface ErrorCalloutProps extends BaseProps {
    /**
     * Position of the Callout - see AnchorPosition
     */
    readonly calloutPosition?: AnchorPosition;

    /**
     * Display error message
     */
    readonly errorMessage: string;

    /**
     * Aria label for the error message
     */
    readonly errorMessageAriaLabel: string;

    /**
     * Call out target or trigger button.
     * If it is a target it will be where the callout will focus on.
     * No trigger button will be shown.
     *
     * OR
     *
     * Aria label and data bi id for trigger button.
     * If it is a trigger button, the callout will focus in the trigger.
     */
    readonly targetOrTriggerButton: RefObject<HTMLElement> | InteractiveElementProps;
}

/**
 * Styles necessary for the div wrapping the Callout's button
 */
const calloutTriggerWrapperStyles: CSSProperties = {
    display: "inline-block",
    verticalAlign: "text-top"
};

export const ErrorCallout: React.FC<ErrorCalloutProps> = ({
    targetOrTriggerButton,
    calloutPosition = "bottom-center",
    errorMessage,
    errorMessageAriaLabel,
    ...otherProps
}) => {
    useComponentLogger("ErrorCallout", otherProps.id);

    const theme = useTheme();
    const [isCalloutVisible, setCalloutVisible] = useState(true);
    const iconButtonRef = useRef<HTMLDivElement>(null);
    let target: RefObject<HTMLElement> | undefined;
    let triggerButton: InteractiveElementProps | undefined;

    if ("aria-label" in targetOrTriggerButton) {
        triggerButton = targetOrTriggerButton;
    } else {
        target = targetOrTriggerButton;
    }

    const calloutTarget = target || iconButtonRef;

    const onCalloutClicked = () => {
        setCalloutVisible(!isCalloutVisible);
    };

    const onCalloutDismiss = () => {
        setCalloutVisible(false);
    };

    validateProps("ErrorCallout", otherProps);

    const iconStyle: Partial<IButtonStyles> = {
        icon: {
            color: theme.semanticColors["errorIcon"]
        },
        root: {
            height: "100%"
        }
    };

    return (
        <>
            {triggerButton && (
                <div>
                    {/*
                        What we want is the callout to focus only on the trigger.
                        Because we use the div ref as target, we want the div to wrap just around the trigger.

                        If the div expand itself to the whole container, the call will have incorrect focus.

                        Therefore, we have inner div takes display: inline-block.

                        However, there is a high chance the parent of the inner div is a flex box.
                        This somehow makes the inner div expand itself. Hence, we introduce an outer div to prevent it.
                    */}
                    <div ref={iconButtonRef} style={calloutTriggerWrapperStyles}>
                        <FluentIconButton
                            onClick={onCalloutClicked}
                            ariaLabel={triggerButton["aria-label"]}
                            data-bi-id={triggerButton["data-bi-id"]}
                            iconProps={{
                                iconName: "Error"
                            }}
                            styles={iconStyle}
                        />
                    </div>
                </div>
            )}
            {isCalloutVisible && (
                <FluentCallout
                    styles={calloutStyles(theme)}
                    role={"alertdialog"}
                    target={calloutTarget}
                    onDismiss={onCalloutDismiss}
                    setInitialFocus={true}
                    directionalHint={anchorPositionToFluentMapping[calloutPosition]}
                    {...otherProps}
                >
                    <Stack horizontal={true} verticalAlign={"center"} childrenGap={6}>
                        {!triggerButton && (<Icon iconName="Error" color="errorIcon" size="16px" />)}
                        <Label aria-label={errorMessageAriaLabel}>{errorMessage}</Label>
                    </Stack>
                </FluentCallout>
            )}
        </>
    );
};
